import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Clock, Video } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { Link } from 'react-router-dom';

interface CountdownTimerProps {
  sessionId: string;
  title: string;
  scheduledAt: string;
  status: string;
}

export function CountdownTimer({ sessionId, title, scheduledAt, status }: CountdownTimerProps) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const sessionTime = new Date(scheduledAt).getTime();
      const difference = sessionTime - now;

      if (difference <= 0 || status === 'live') {
        setIsLive(true);
        return null;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      const time = calculateTimeLeft();
      setTimeLeft(time);
    }, 1000);

    return () => clearInterval(timer);
  }, [scheduledAt, status]);

  if (status === 'completed' || status === 'cancelled') {
    return null;
  }

  return (
    <Card className="border-2 border-blue-500 bg-gradient-to-br from-blue-50 to-blue-100">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5 text-blue-600" />
          <CardTitle className="text-lg">Live Session Programmata</CardTitle>
        </div>
        <CardDescription className="text-base font-semibold text-gray-900">
          {title}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-sm text-gray-700">
          <Clock className="w-4 h-4" />
          <span>
            {format(new Date(scheduledAt), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
          </span>
        </div>

        {isLive ? (
          <div className="space-y-3">
            <div className="p-4 bg-green-500 text-white rounded-lg text-center font-bold animate-pulse">
              LA LIVE SESSION E IN CORSO!
            </div>
            <Link to={`/dashboard/live/${sessionId}`}>
              <Button className="w-full bg-green-600 hover:bg-green-700" size="lg">
                <Video className="w-5 h-5 mr-2" />
                Partecipa ora
              </Button>
            </Link>
          </div>
        ) : timeLeft ? (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{timeLeft.days}</div>
                <div className="text-xs text-gray-600 uppercase">Giorni</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{timeLeft.hours}</div>
                <div className="text-xs text-gray-600 uppercase">Ore</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{timeLeft.minutes}</div>
                <div className="text-xs text-gray-600 uppercase">Min</div>
              </div>
              <div className="bg-white rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{timeLeft.seconds}</div>
                <div className="text-xs text-gray-600 uppercase">Sec</div>
              </div>
            </div>
            <p className="text-xs text-center text-gray-600">
              Riceverai una notifica quando la live session iniziera
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
